/**
 * "Task data delayed" — wired to the source that can prove it, and the overdue
 * count that has no source at all.
 *
 * ── Three facts, three sentences, never merged ──────────────────────────────
 *
 * `openapi.yaml` is explicit that merging `degraded` and `cache_status` "would leave
 * a caller unable to tell 'achau is down' from 'the sweep is late' — two situations
 * needing different actions from different people". Task 30's own report says its
 * cache *"says what it can prove, not what it suspects"*. So:
 *
 *   `degraded`      achau could not be reached **during this request**. Positive
 *                   evidence — the breaker opens only after five consecutive
 *                   availability failures. → `DataDelayed source='achau'`.
 *   `cache_status`  the state of this company's `sync_tasks` sweep: `fresh`,
 *                   `stale`, `never_synced`. An operator's problem, not an empty
 *                   list. → its own sentence naming which of the three.
 *   `cached_at`     the newest sync instant across the rows, which is spec 01 §12's
 *                   "nhãn thời điểm sync".
 *
 * **Nothing here compares `Date.now()` to a timestamp to decide that data is late.**
 * Staleness is the server's answer, carried through. A clock in the browser would
 * turn a laptop with a wrong clock into an outage report.
 *
 * ── There is no overdue count, and the tile says why ────────────────────────
 *
 * `GET /tasks/search` is a search: `q` is `required` with `minLength: 1`, the scope
 * is `self`, `assignee_id` narrows to one employee, and there is no due-date filter
 * and no aggregate. A count built from it would be the size of whatever an invented
 * query string happened to rank. So the tile prints the position, the endpoint that
 * would close it, and no number — the ruling Task 43 took for the export button and
 * Task 42 for the policy acknowledgement.
 *
 * The one request it *does* make is a real search, for a token the copy names, and
 * its result rows are **not rendered**: the tile reads only the envelope. That is
 * the honest shape available today — the response-level freshness fields are real
 * and are exactly what spec 01 §12's acceptance criterion asks for.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstantWithAge } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import type { TaskSearchResponse } from '../../api/types';
import { TASK_CACHE_PROBE_LIMIT, TASK_CACHE_PROBE_QUERY, TASK_SEARCH_PATH } from '../../api/paths';
import { DataDelayed } from '../../states/DataDelayed';
import { Tile } from './Tile';

const CACHE_KEY: Readonly<Record<TaskSearchResponse['cache_status'], MessageKey>> = {
  fresh: 'overview.task.cache.fresh',
  stale: 'overview.task.cache.stale',
  never_synced: 'overview.task.cache.never_synced',
};

/** The newest `cached_at` in the answer, or `null` when no row carries one. */
export function newestCachedAt(response: TaskSearchResponse | null): string | null {
  let newest: string | null = null;
  for (const row of response?.data ?? []) {
    const at = row.cached_at;
    if (at === null || at === undefined) continue;
    if (newest === null || at > newest) newest = at;
  }
  return newest;
}

export function TaskDataTile({
  response,
  timezone,
}: {
  readonly response: TaskSearchResponse | null;
  readonly timezone: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const syncedAt = newestCachedAt(response);

  return (
    <Tile
      title={t('overview.task.title')}
      // The caps key said "DỮ LIỆU CÔNG VIỆC TỪ ACHAU" directly above a heading that
      // said "Dữ liệu công việc: …". One of the two had to go and the heading is the
      // one carrying the state (finish review, fix 7); the key stays for a screen
      // reader, which has no 11px/19px distinction to read it by.
      titleHidden
      // Stale or degraded is a thing to act on; "chưa đồng bộ lần nào" is an absence,
      // and an absence rests on plain paper like the three figures above it (Luật Màu
      // Có Nghĩa; finish review, round 2, fix 2).
      attention={response !== null && (response.cache_status === 'stale' || response.degraded)}
      figure={
        <span className="wp-figure wp-figure--words" data-testid="k4-task-cache">
          {response === null ? t('overview.neverUpdated') : t(CACHE_KEY[response.cache_status])}
        </span>
      }
      detail={
        <>
          <p className="wp-small" data-testid="k4-task-synced-at">
            {syncedAt === null
              ? t('overview.task.noSyncedAt')
              : t('overview.task.syncedAt', {
                  at: formatInstantWithAge(syncedAt, locale, timezone, t('common.never')),
                })}
          </p>
          <p className="wp-small wp-muted">{t('overview.task.rest')}</p>
          {/* The probe is stated on the tile rather than buried in a source comment:
              a reader who assumes this came from a tidy sweep-status endpoint would
              read the sync instant as stronger than it is. */}
          <p className="wp-small wp-muted" data-testid="k4-task-probe">
            {t('overview.task.probe', {
              request: `GET ${TASK_SEARCH_PATH}?q=${TASK_CACHE_PROBE_QUERY}&limit=${String(TASK_CACHE_PROBE_LIMIT)}`,
            })}
          </p>
        </>
      }
      caveat={
        <>
          {response?.degraded === true ? (
            <DataDelayed
              source="achau"
              since={syncedAt}
              timezone={timezone}
              detail={t('overview.task.degraded')}
              testId="k4-task-degraded"
            />
          ) : null}
          <div className="wp-slot" data-testid="k4-task-overdue">
            <h4 className="wp-slot__title">{t('overview.task.overdue.title')}</h4>
            <p className="wp-small">{t('overview.task.overdue.body')}</p>
            <p className="wp-small wp-muted">{t('overview.task.overdue.needs')}</p>
          </div>
        </>
      }
      testId="k4-task"
    />
  );
}
